import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  HelpCircle, ChevronRight, ArrowLeft, Search as SearchIcon,
  Building2, User, ShieldCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CAT_ICONS = {
  general: HelpCircle,
  visitor: User,
  establishment: Building2,
  privacy: ShieldCheck,
};

export default function HelpFAQ() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("general");
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/help/faq`).then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="faq-loading">
        Chargement de la FAQ…
      </div>
    );
  }

  const cat = data.categories.find((c) => c.id === active) || data.categories[0];
  const q = query.trim().toLowerCase();
  const filtered = q
    ? cat.questions.filter(
        (it) =>
          it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q),
      )
    : cat.questions;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="faq-page">
      <div className="bg-gradient-to-br from-[#002FA7] to-[#003FC7] text-white">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6"
            data-testid="faq-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>
          <p className="uppercase tracking-widest text-white/60 text-xs mb-2">Aide en ligne</p>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight">{data.title}</h1>
          <p className="text-white/80 mt-3 text-lg max-w-2xl">{data.subtitle}</p>

          <div className="mt-8 relative max-w-xl">
            <SearchIcon className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher une question…"
              className="w-full bg-white text-slate-900 pl-12 pr-4 py-3 rounded-lg shadow-lg outline-none focus:ring-2 focus:ring-white/40"
              data-testid="faq-search-input"
            />
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to="/help/visitor"
              className="text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition"
              data-testid="faq-link-visitor"
            >
              Guide Visiteur complet →
            </Link>
            <Link
              to="/help/establishment"
              className="text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition"
              data-testid="faq-link-establishment"
            >
              Guide Établissement complet →
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 grid md:grid-cols-[240px_1fr] gap-8">
        <aside className="md:sticky md:top-6 self-start space-y-1" data-testid="faq-sidebar">
          {data.categories.map((c) => {
            const I = CAT_ICONS[c.id] || HelpCircle;
            return (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition ${
                  active === c.id
                    ? "bg-[#002FA7] text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
                data-testid={`faq-cat-${c.id}`}
              >
                <I className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{c.title}</span>
                <span className="ml-auto text-xs opacity-60">{c.questions.length}</span>
              </button>
            );
          })}
        </aside>

        <main className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8" data-testid="faq-content">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">{cat.title}</h2>
          {filtered.length === 0 ? (
            <p className="text-slate-500 italic">Aucune question ne correspond à votre recherche.</p>
          ) : (
            <div className="space-y-2">
              {filtered.map((item, i) => (
                <details
                  key={i}
                  className="bg-slate-50 rounded-lg border border-slate-200 group"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="cursor-pointer px-4 py-3 font-medium text-slate-900 hover:bg-slate-100 rounded-lg list-none flex items-center justify-between">
                    <span>{item.q}</span>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition" />
                  </summary>
                  <p className="px-4 pb-4 text-slate-600">{item.a}</p>
                </details>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
